/**
 * [Insider+] Membership-management page — hosts the iframe at /membership.
 */

import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { INSIDER_PLUS_BASE_URL } from '../security/config';
import InsiderPlusIframeShell from '../components/InsiderPlus/InsiderPlusIframeShell';
import { clearInsiderPlusPath } from '../util/helpers/insiderPlusPathHelper';
import useInsiderPlusIframe from '../util/hooks/useInsiderPlusIframe';

const InsiderPlusMembershipPage = () => {
  const history = useHistory();

  const buildIframeSrc = useCallback(
    () => `${INSIDER_PLUS_BASE_URL}/membership`,
    [],
  );

  // Same-origin targets (e.g. iframe breadcrumb back to /insider-plus-welcome)
  // go through react-router so the parent tree doesn't full-reload, and clear
  // the stored deep path so Welcome doesn't bounce the user back to the page
  // they just left. External URLs (e.g. Discourse forum) fall back to
  // window.location.href and preserve the stored path — the user is leaving
  // the app and should land back on their last iframe page on return.
  const onNavigateUrl = useCallback(
    (url) => {
      try {
        const target = new URL(url, window.location.origin);
        if (target.origin === window.location.origin) {
          clearInsiderPlusPath();
          history.push(target.pathname + target.search + target.hash);
          return;
        }
      } catch {
        // Malformed URL — fall through to full-page navigation
      }
      window.location.href = url;
    },
    [history],
  );

  const {
    iframeRef,
    iframeSrc,
    iframeKey,
    isRecovering,
    authFailed,
    isRetrying,
    handleIframeLoad,
    handleRetry,
  } = useInsiderPlusIframe({ buildIframeSrc, onNavigateUrl });

  return (
    <InsiderPlusIframeShell
      iframeRef={iframeRef}
      iframeSrc={iframeSrc}
      iframeKey={iframeKey}
      title="Insider Plus Membership"
      dataCy="container-insider-plus-membership-page"
      isRecovering={isRecovering}
      authFailed={authFailed}
      isRetrying={isRetrying}
      onIframeLoad={handleIframeLoad}
      onRetry={handleRetry}
    />
  );
};

export default InsiderPlusMembershipPage;
